import { TimeEntry } from '../components/TimeTracking/types';
import { ProjectPlanEntry } from '../components/TimeTracking/ProjectPlan/types';

const STORAGE_KEY = 'timeEntries';
const TAGS_KEY = 'timeTags';
const PROJECT_PLANS_KEY = 'projectPlans';
const STAGES_KEY = 'kanbanStages';
const KANBAN_TASKS_KEY = 'kanbanTasks';

const DEFAULT_KANBAN_TASKS = [
  {
    id: '1',
    title: 'Criar wireframes',
    description: 'Desenvolver wireframes para a nova interface',
    status: 'todo',
    startDate: '2024-03-15',
    project: 'web',
    priority: 'high',
    assignee: 'Ana Silva',
    dueDate: '2024-03-30',
    tags: ['design', 'ui/ux']
  },
  {
    id: '2',
    title: 'Implementar autenticação',
    description: 'Adicionar sistema de login com OAuth',
    status: 'in-progress',
    startDate: '2024-04-01',
    project: 'app',
    priority: 'medium',
    assignee: 'Carlos Santos',
    dueDate: '2024-04-15',
    tags: ['backend', 'security']
  },
  {
    id: '3',
    title: 'Testes de usabilidade',
    description: 'Conduzir testes com usuários finais',
    status: 'done',
    startDate: '2024-03-20',
    project: 'campaign',
    priority: 'low',
    assignee: 'Maria Costa',
    dueDate: '2024-04-01',
    tags: ['testing']
  }
];

const DEFAULT_PROJECT_PLANS: ProjectPlanEntry[] = [
  {
    id: 1,
    project: 'Website Redesign',
    plannedHours: 40,
    startDate: '2024-03-15',
    endDate: '2024-04-15',
    teamPlans: [
      { memberId: 1, name: 'Ana Silva', role: 'Designer Senior', plannedHours: 20 },
      { memberId: 2, name: 'Carlos Santos', role: 'Desenvolvedor', plannedHours: 20 }
    ],
    status: 'in-progress',
  },
  {
    id: 2,
    project: 'Mobile App',
    plannedHours: 80,
    startDate: '2024-04-01',
    endDate: '2024-05-30',
    teamPlans: [
      { memberId: 2, name: 'Carlos Santos', role: 'Desenvolvedor', plannedHours: 40 },
      { memberId: 4, name: 'João Pedro', role: 'Designer UI/UX', plannedHours: 40 }
    ],
    status: 'pending',
  },
];

const DEFAULT_TIME_ENTRIES = [
  {
    id: 1,
    project: 'Website Redesign',
    description: 'Design da página inicial',
    duration: 14400, // 4 hours in seconds
    date: new Date().toISOString(),
    startTime: '09:00',
    endTime: '13:00',
    billable: true,
    tag: '1',
    assignedTo: 1
  },
  {
    id: 2,
    project: 'Mobile App',
    description: 'Desenvolvimento de componentes',
    duration: 21600, // 6 hours in seconds
    date: new Date().toISOString(),
    startTime: '14:00',
    endTime: '20:00',
    billable: true,
    tag: '2',
    assignedTo: 2
  }
];
const DEFAULT_TAGS = [
  { id: 1, name: 'Interno', color: '#3B82F6', archived: false },
  { id: 2, name: 'Cliente', color: '#10B981', archived: false },
  { id: 3, name: 'Reunião', color: '#F59E0B', archived: false }
];

const DEFAULT_STAGES = [
  { id: 'todo', name: 'A Fazer', order: 0 },
  { id: 'in-progress', name: 'Em Andamento', order: 1 },
  { id: 'done', name: 'Concluído', order: 2 }
];

// Armazenamento em memória global
const memoryStorage: Record<string, string> = {};

// Guardar referência ao localStorage original
const originalLocalStorage = window.localStorage;

// Exportar um objeto que substitui completamente localStorage
export const safeStorage = {
  getItem: (key: string): string | null => {
    try {
      return originalLocalStorage.getItem(key); // Usar a referência original
    } catch (e) {
      console.log(`Error accessing localStorage, using memory storage: ${e}`);
      return memoryStorage[key] || null;
    }
  },
  
  setItem: (key: string, value: string): void => {
    memoryStorage[key] = value; // Sempre armazena em memória
    try {
      originalLocalStorage.setItem(key, value); // Usar a referência original
    } catch (e) {
      console.log(`Could not set in localStorage: ${e}`);
    }
  },
  
  removeItem: (key: string): void => {
    delete memoryStorage[key]; // Remove da memória
    try {
      originalLocalStorage.removeItem(key); // Usar a referência original
    } catch (e) {
      console.log(`Could not remove from localStorage: ${e}`);
    }
  }
};

// Substituir o localStorage globalmente
try {
  Object.defineProperty(window, 'localStorage', {
    value: safeStorage,
    writable: false
  });
  console.log("localStorage substituído com sucesso");
} catch (e) {
  console.error("Não foi possível substituir localStorage:", e);
}

export const loadEntries = (): TimeEntry[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsedEntries = stored ? JSON.parse(stored) : null;
    if (!parsedEntries || !Array.isArray(parsedEntries) || parsedEntries.length === 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_TIME_ENTRIES));
      return DEFAULT_TIME_ENTRIES;
    }
    return parsedEntries;
  } catch (error) {
    console.error('Error loading entries from localStorage:', error);
    return DEFAULT_TIME_ENTRIES;
  }
};

export const saveEntries = (entries: TimeEntry[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    // Only dispatch event if called from TimeTrackerPopup
    if (entries.length > 0 && entries[0].description) {
      window.dispatchEvent(new Event('timeEntriesUpdated'));
    }
  } catch (error) {
    console.error('Error saving entries to localStorage:', error);
  }
};

export const loadTags = () => {
  try {
    const stored = localStorage.getItem(TAGS_KEY);
    const parsedTags = stored ? JSON.parse(stored) : null;
    if (!parsedTags || !Array.isArray(parsedTags) || parsedTags.length === 0) {
      localStorage.setItem(TAGS_KEY, JSON.stringify(DEFAULT_TAGS));
      return DEFAULT_TAGS;
    }
    return parsedTags;
  } catch (error) {
    console.error('Error loading tags from localStorage:', error);
    return DEFAULT_TAGS;
  }
};

export const saveTags = (tags: any[]): void => {
  try {
    localStorage.setItem(TAGS_KEY, JSON.stringify(tags));
    // Dispatch a custom event to notify other components
    window.dispatchEvent(new Event('tagsUpdated'));
  } catch (error) {
    console.error('Error saving tags to localStorage:', error);
  }
};

export const loadProjectPlans = (): ProjectPlanEntry[] => {
  try {
    const stored = localStorage.getItem(PROJECT_PLANS_KEY);
    const parsedPlans = stored ? JSON.parse(stored) : null;
    if (!parsedPlans || !Array.isArray(parsedPlans) || parsedPlans.length === 0) {
      localStorage.setItem(PROJECT_PLANS_KEY, JSON.stringify(DEFAULT_PROJECT_PLANS));
      return DEFAULT_PROJECT_PLANS;
    }
    return parsedPlans;
  } catch (error) {
    console.error('Error loading project plans from localStorage:', error);
    return DEFAULT_PROJECT_PLANS;
  }
};

export const saveProjectPlans = (plans: ProjectPlanEntry[]): void => {
  try {
    localStorage.setItem(PROJECT_PLANS_KEY, JSON.stringify(plans));
  } catch (error) {
    console.error('Error saving project plans to localStorage:', error);
  }
};

export const loadStages = () => {
  try {
    const stored = localStorage.getItem(STAGES_KEY);
    const parsedStages = stored ? JSON.parse(stored) : null;
    if (!parsedStages || !Array.isArray(parsedStages) || parsedStages.length === 0) {
      localStorage.setItem(STAGES_KEY, JSON.stringify(DEFAULT_STAGES));
      return DEFAULT_STAGES;
    }
    return parsedStages;
  } catch (error) {
    console.error('Error loading stages from localStorage:', error);
    return DEFAULT_STAGES;
  }
};

export const saveStages = (stages: any[]): void => {
  try {
    localStorage.setItem(STAGES_KEY, JSON.stringify(stages));
  } catch (error) {
    console.error('Error saving stages to localStorage:', error);
  }
};

export const loadKanbanTasks = () => {
  try {
    const stored = localStorage.getItem(KANBAN_TASKS_KEY);
    const parsedTasks = stored ? JSON.parse(stored) : null;
    if (!parsedTasks || !Array.isArray(parsedTasks) || parsedTasks.length === 0) {
      localStorage.setItem(KANBAN_TASKS_KEY, JSON.stringify(DEFAULT_KANBAN_TASKS));
      return DEFAULT_KANBAN_TASKS;
    }
    return parsedTasks;
  } catch (error) {
    console.error('Error loading kanban tasks from localStorage:', error);
    return DEFAULT_KANBAN_TASKS;
  }
};

export const saveKanbanTasks = (tasks: any[]): void => {
  try {
    localStorage.setItem(KANBAN_TASKS_KEY, JSON.stringify(tasks));
  } catch (error) {
    console.error('Error saving kanban tasks to localStorage:', error);
  }
};